import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Block } from 'app/interfaces/blocks';
import { AppInjector } from 'app/app.module';
import { Blocks } from 'app/main/pages/general-editor-tabs/blocks/blocks';
import uuid from 'short-unique-id';
import { GlobalService } from 'app/global/global.service';
import { DomListenerService } from 'app/global/dom-listener.service';
import { DomHelperService } from 'app/global/dom-helper.service';
import { UploadsService } from 'app/global/uploads.service';
import { TranslocoService } from '@jsverse/transloco';
import { EditorEmitterService } from '../events/editor-emitter.service';
import { Router } from '@angular/router';
import { resetRelation } from 'app/global/fieldrelation';
import { DomHandlersService } from 'app/global/dom-handlers.service';
import { SessionImageService } from 'app/global/session-image.service';
import { FieldSettings } from '../events/editor-emitter.dto';
import { Field } from 'app/interfaces/fields';
import { RefreshImageService } from 'app/global/refresh-image.service';
import { HelperService } from './helper.service';
import { IBrandingDto } from '../settings/branding/branding.dto';
import { PopoverSettingMenu } from './edit-template.dto';
import { FieldRenderService } from './field-render.service';
import { AreaType } from 'app/global/dom-handler.dto';
import { of, delay, take, Subscription, expand, filter } from 'rxjs';
import { ReusableAttachmentService } from 'app/global/reusable-attachment.service';
import { productTable } from '../general-editor-tabs/product-table/productTable';

declare const $;
interface Options {
    template_id: number | string,
    section_key?: string,
}


interface PDFTOIMAGE {
    original : string,
    uuid?: string|number
}

@Injectable({
    providedIn: 'root'
})

export class EventHandlersService {
    private renderer: Renderer2;
    public uuid = new uuid({ length: 6, dictionary: 'number' });
    public attachmentType: Record<'document_id' | 'template_id' | 'content_id', string> = {
        document_id: 'document',
        template_id: 'templates',
        content_id: 'contentlibrary'
    };
    private timeOutSubscription: Subscription;
    private pdfUploadSubscription: Subscription | null = null;
    constructor(
        private globalService: GlobalService,
        rendererFactory: RendererFactory2,
        private Blocks: Blocks,
        private domListenerService: DomListenerService,
        private uploads: UploadsService,
        private translocoService: TranslocoService,
        private editorEmitterService: EditorEmitterService,
        private router: Router,
        private domHandlersService: DomHandlersService,
        private sessionImageService: SessionImageService,
        private helperService: HelperService,
        private refreshImageService: RefreshImageService,
        private _fieldRenderService: FieldRenderService,
        private reusableAttachmentService: ReusableAttachmentService,
        public productTable: productTable
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    initEvents(options: Options) {
        this.uploadAttachment();
        this.attachmentUploader(options.template_id, options.section_key);
        this.uploadPdfToHtml();
        this.pdfUploader(options.template_id, options.section_key);
    }

    uploadAttachment() {
        this.domListenerService.addListener(document.querySelectorAll('.attachment-block-upload .attachment-boxInner'), 'click', (event) => {
            const element = event.target as HTMLInputElement;
            const uuid = element.closest('.attachment-block-upload').getAttribute('uuid');
            const attachmentBlock: Block = this.globalService.fields_data['blocks'][uuid];
            if (element.nodeName != 'A' && [0, -1].includes(attachmentBlock?.request_from)) {
                const input = document.querySelector('input[name="' + uuid + '"]');
                this.renderer.selectRootElement(input).click();
            }
        });
    }

    /**
    * upload a file for attachment
    * @param event 
    * @param options 
    */
    attachmentUploader(template_id: number | string, key: string) {
        const domHelperService = AppInjector.get(DomHelperService);
        this.domListenerService.addListener('input.attachment-uploader', 'change', (event) => {
            const element = event.target as HTMLInputElement;
            const attacheduuid = element.getAttribute('name');
            const attachmentBlockData: Block = this.globalService.fields_data['blocks'][attacheduuid];
            const files = element.files;
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                // Validate file size
                if (!this.globalService.validateFileSize(file)) {
                    // If a file exceeds the limit, alert and stop further processing
                    this.globalService.toastrWarning(this.translocoService.translate("Toaster message for upload attachments (Template/Document/Content library section) - File exceeds the 20 MB size limit", {filename: file.name}));
                    return;  // Exit the function and stop processing
                }
            }
            this.renderer.setStyle(document.body, 'cursor', 'wait');
            const uploadPdfLoader = document.querySelector('.upload-pdf-loader');
            if (uploadPdfLoader) {
                this.renderer.removeClass(uploadPdfLoader, 'hidden');
            }
            const currentRoute = this.router.url.split('/')[2];
            if(currentRoute === 'edit-content'){
                key = 'content_id';
            }
            const formData: FormData = this.uploads.collectFormData(files);
            formData.append('location', `${this.attachmentType[key]}/${template_id}/attachments/${attacheduuid}`);
            formData.append('unlink_folder', `${this.attachmentType[key]}/${template_id}/attachments/${attacheduuid}`);
            formData.append(key, `${template_id}`);
            formData.append('upload_type', (key == 'template_id' ? 'template_attachment' : (key === 'content_id' ? 'content_attachment' : 'document_attachment')));
            formData.append('uuid', attacheduuid);
            this.uploads.uploadFile(formData).subscribe({
                next: (files) => {
                    const attachmentBlockUpload = document.querySelector(`.attachment-block-upload[uuid="${attacheduuid}"]`);
                    const attachmentBeforeUpload = attachmentBlockUpload.querySelector('.attachment-before-upload');
                    const attachmentAfterUpload = attachmentBlockUpload.querySelector('.attachment-after-upload');
                    const attachmentListItems = attachmentAfterUpload.querySelector('.attachment-list-items');
                    this.renderer.addClass(attachmentBeforeUpload, 'hidden');
                    this.renderer.removeClass(attachmentAfterUpload, 'hidden');
                    let fileView = '';
                    if (Array.isArray(files)) {
                        for (let index = 0; index < files.length; index++) {
                            const uuid = this.uuid();
                            files[index].attachmentUrl = this.sessionImageService.getTemplateImages(files[index]?.url, Number(template_id), this.attachmentType[key]);
                            fileView += this.Blocks.attachmentLinkView(files[index], uuid, attacheduuid, files[index].filename);
                            files[index] = { ...{ uuid: uuid }, ...files[index] };
                            this.globalService.updateBlock(attacheduuid, { files: files, attachment_name: files[index].filename, is_uploaded: 1});
                            this.reusableAttachmentService.patchReusableAttachment({
                                roleId: attachmentBlockData?.role_id,
                                metaDataId: attachmentBlockData?.meta_data_id,
                                uuid: attacheduuid,
                            });
                        }
                    }
                    this.renderer.setProperty(attachmentListItems, 'innerHTML', fileView);
                    element.value = null;
                    this.renderer.setStyle(document.body, 'cursor', 'default');
                    if (uploadPdfLoader) {
                        this.renderer.addClass(uploadPdfLoader, 'hidden');
                    }
                    domHelperService.initializeEventListener('.delete-attachment-file', 'click', 'deleteAttachment');
                },
                error: (error) => {
                    this.globalService.toastrError(error);
                    this.renderer.setStyle(document.body, 'cursor', 'default');
                    if (uploadPdfLoader) {
                        this.renderer.addClass(uploadPdfLoader, 'hidden');
                    }
                }
            })
        });
        domHelperService.initializeEventListener('.delete-attachment-file', 'click', 'deleteAttachment');
    }

    /**
     * select the pdf file to upload
     */
    uploadPdfToHtml() {
        this.domListenerService.addListener('.pdf-block-upload .attachment-boxInner', 'click', (event) => {
            const element = event.target as HTMLInputElement;
            const uuid = element.closest('.pdf-block-upload').getAttribute('uuid');
            const pdfBlock: Block = this.globalService.fields_data['blocks'][uuid];
            if (element.nodeName != 'A' && [0, -1].includes(pdfBlock.request_from)) {
                const input = document.querySelector('input[name="' + uuid + '"]');
                this.renderer.selectRootElement(input).click();
            }
        });
    }

    /**
     * upload the pdf file and convert into the html
     */
    pdfUploader(template_id: number | string, key: string) {
        this.domListenerService.addListener('.pdf-block-uploader', 'change', (event) => {
            const element = event.target as HTMLInputElement;
            const pdfUuid = element.getAttribute('name');
            const pdfBlockData = this.globalService.fields_data['blocks'][pdfUuid];
            const pdfDom = element.closest('.upload-pdf-block');
            if(!pdfDom){
                return;
            }
            let blockData = {};
            if (element.files.length > 0) {
                const uploadPdfLoader = document.querySelector('.upload-pdf-loader');
                if (uploadPdfLoader) {
                    this.renderer.removeClass(uploadPdfLoader, 'hidden');
                }
                const currentRoute = this.router.url.split('/')[2];
                if(currentRoute === 'edit-content'){
                    key = 'content_id';
                }
                const formData: FormData = this.uploads.collectFormData(element.files, "file");
                this.uploads.uploadDocument(formData).subscribe({
                    next: ({ task_id }: { task_id: string }) => {
                        if (!task_id) {
                            element.value = null;
                            this.globalService.toastrError('Upload failed');
                            if (uploadPdfLoader) {
                                this.renderer.addClass(uploadPdfLoader, 'hidden');
                            }
                            return;
                        }

                        // Unsubscribe from any previous polling subscription
                        if (this.pdfUploadSubscription) {
                            this.pdfUploadSubscription.unsubscribe();
                        }

                        this.pdfUploadSubscription = this.uploads.trackUploadedDocumentStatus(task_id).pipe(
                            expand(() =>
                                this.uploads.trackUploadedDocumentStatus(task_id).pipe(
                                    delay(3000)
                                )
                            ),
                            filter(({ status }: any) => ['complete', 'completed', 'fail', 'failed'].includes(status)),
                            take(1)
                        ).subscribe({
                            next: ({ status, images }: { status: string, images?: PDFTOIMAGE[] }) => {
                                if (status === 'fail' || status === 'failed') {
                                    element.value = null;
                                    this.globalService.toastrError('Upload failed');
                                    if (uploadPdfLoader) {
                                        this.renderer.addClass(uploadPdfLoader, 'hidden');
                                    }
                                    return;
                                }

                                const pdfToImage = images ?? [];
                                if (Array.isArray(pdfToImage) && pdfToImage.length > 0) {
                                    const { block, pdfBlock } = this.generatePdfBlocks(pdfToImage, Number(template_id), this.attachmentType[key], pdfUuid, pdfBlockData, true);
                                    blockData = block;
                                    this.renderer.setProperty(pdfDom, 'outerHTML', pdfBlock);
                                    element.value = null;
                                    if (uploadPdfLoader) {
                                        this.renderer.addClass(uploadPdfLoader, 'hidden');
                                    }
                                    this.editorEmitterService.triggerEmitter({ initevents: true });
                                    this.globalService.updateBlock(pdfUuid, { is_parent: 1, files: pdfToImage });

                                    // Run the complete logic here
                                    for (const key in blockData) {
                                        const blockDataItem = this.globalService.fields_data['blocks'][key];
                                        const pdfUrl = this.sessionImageService.getTemplateImages(blockDataItem?.url, Number(template_id), this.attachmentType[key]);
                                        this.domHandlersService.setPdfBlockHeight(pdfUrl, blockDataItem.uuid);
                                    }
                                } else {
                                    element.value = null;
                                    this.globalService.toastrError('Upload failed');
                                    if (uploadPdfLoader) {
                                        this.renderer.addClass(uploadPdfLoader, 'hidden');
                                    }
                                }
                            },
                            error: () => {
                                element.value = null;
                                this.globalService.toastrError('Upload failed');
                                if (uploadPdfLoader) {
                                    this.renderer.addClass(uploadPdfLoader, 'hidden');
                                }
                            }
                        });
                    },
                    error: (error) => {
                        element.value = null;
                        this.globalService.toastrError(error);
                        if (uploadPdfLoader) {
                            this.renderer.addClass(uploadPdfLoader, 'hidden');
                        }
                    }
                });
            }
        });
    }

    // global.service.ts

    generatePdfBlocks(pdfToImage: PDFTOIMAGE[], template_id: number, attachmentType: string, pdfUuid: string, pdfBlockData, isEditor: boolean = false)
    {
        let page = 1;
        let pdfBlock = '';
        const block = {};
        for (const url of pdfToImage) {
            const pdfUrl = isEditor ? this.sessionImageService.getTemplateImages(url.original, Number(template_id), attachmentType) : undefined;

            const { pdfView, uuid } = this.Blocks.pdfViewBlock(
                { 
                    url: url.original,
                    contentable: true, 
                    pdfUrl:pdfUrl
                }
            );
            // Append the PDF view to the block
            pdfBlock += pdfView;
            // Reset the relation for the PDF block
            if(isEditor)resetRelation(pdfUuid, this.globalService);
            block[uuid] = {
                ...pdfBlockData,
                uuid,
                connected_parent_conditional_id: [],
                url: url.original,
                parent_uuid: pdfUuid,
                page,
                is_parent: 0,
            };

            url.uuid = uuid;
            page++;
        }
        this.globalService.fields_data['blocks'] = {
            ...this.globalService.fields_data['blocks'],
            ...block,
        };
        return { pdfBlock, block };
    }

    initListeners(id :number, route :string, branding :IBrandingDto) {
        for (let index = 0; index < document.querySelectorAll(`.content-box-inner`).length; index++) {
            const element = document.querySelectorAll(`.content-box-inner`)[index];
            let uuid = element.getAttribute('editor-uuid');
            const type = element.getAttribute('type');
            if (type == 'content box') {
              setTimeout(() => this.helperService.updateDateFormatInDatePicker(), 500);
            } else if (type == 'datepicker block') {
              this.globalService.initializeFlatpicker(document.querySelectorAll('.editor' + uuid + ' .datepicker-field'));
            } else if (type == 'form') {
              this.globalService.initializeFlatpicker(document.querySelectorAll('.editor' + uuid + ' .form-builder-parent-container .datepicker-field'));
            } else if (type == 'image') {
              uuid = element.getAttribute('uuid');
              this.globalService.initializeContentImageResize('[uuid="' + uuid + '"] .content-selected-image');
            } else if (type == 'table') {
              setTimeout(() => {
                this.helperService.updateDateFormatInDatePicker();
              }, 500);
            }
          }
          for (let index = 0; index < document.querySelectorAll('[pdfblock="1"]').length; index++) {
            const element = document.querySelectorAll('[pdfblock="1"]')[index];
            const uuid = element.getAttribute('uuid');
            const field: Field = this.globalService.fields_data[uuid];
            this.globalService.initializeDragOnEditorFields(field?.field, $('.drag-fields[uuid="' + uuid + '"]'));
          }
          this.initRenderListners(id, route);
          this.refreshImageService.collectTheImagesOfTemplate(this.globalService.getTheSectionByRoute(route).toLowerCase(), id, branding);
          this.globalService.contentBoxSort();
    }

      /**
      * this methoe will be use in the future, above jquery code will be remove 
      */
    initRenderListners(id: number, currentRoute: string) {
        const domHelperService = AppInjector.get(DomHelperService);
        domHelperService.initializeEventListener('body', 'click', 'bodyClickHandler');
        domHelperService.initializeEventListener('.content-box-inner', 'click', 'contentBoxInnerHandler');
        domHelperService.initializeEventListener('.content-field-inner', 'click', 'openFieldSettings');
        domHelperService.initializeEventListener('.generate-signature', 'click', 'generateSignatureForSender');
        domHelperService.initializeEventListener('.content-box', 'mouseenter', 'mouseEnterOnContentBox');
        domHelperService.initializeEventListener('.content-box', 'mouseleave', 'mouseLeaveOnContentBox');
        domHelperService.initializeEventListener('.rowItem', 'mouseenter', 'mouseEnterOnContentBoxInner');
        domHelperService.initializeEventListener('.rowItem', 'mouseleave', 'mouseLeaveOnContentBoxInner');
        domHelperService.initializeEventListener('.datepicker-field.content-draggable-field', 'mousedown', 'openDatepicker');
        this.initEvents({ template_id: id, section_key: (currentRoute === 'send-document' ? 'document_id' : currentRoute === 'edit-content' ? 'content_id' : 'template_id') });
        domHelperService.initializeEventListener('.content-library-modal-setting', 'click', 'openContentLibraryModal');
        domHelperService.initializeEventListener(document.querySelectorAll('.text-field-input'), 'focus', 'onTextFieldFocus', { role_id: 0, area: AreaType.Editor });
        domHelperService.initializeEventListener(document.querySelectorAll('.text-field-input'), 'click', 'onTextFieldFocus', { role_id: 0, area: AreaType.Editor });
        domHelperService.initializeEventListener(document.querySelectorAll('.upload-image-modal-setting'), 'click', 'uploadImageSetting');
        this.updatePDFBlockHeight(id, currentRoute);
        this.setTheBlockSortElement();
        this.timeOutSubscription = of(null)
        .pipe(delay(1000), take(1))
        .subscribe(() => {
        domHelperService.initializeEventListener(document.querySelectorAll('.droplist-menu'), 'click', 'openDropListMenu', { role_id: 0 });
        domHelperService.initializeEventListener(document.querySelectorAll('.search-drop-value'), 'keyup', 'searchDropDownLabelOnKeyUp', { role_id: 0 });
        domHelperService.initializeEventListener(document.querySelectorAll('.multiChoice-box'), 'click', 'multiChoiceAction', { role_id: 0 });
        domHelperService.initializeEventListener(document.querySelectorAll('.checkbox-field-box'), 'click', 'toggleCheckBox', { role_id: 0 });
        });
        domHelperService.initializeEventListener('.open-tag-popover', 'click', 'openTagPopover', {role_id:0, hide_settings:false});
        this.domListenerService.addListener('.pdf-block-editor-box', 'click', (event:MouseEvent) => {
        const target = event.target as HTMLElement;
        const uuid = target.closest('.content-box-inner').getAttribute('editor-uuid');
        this.triggerEditorSideMenu({ uuid: uuid }, PopoverSettingMenu.pdfSettingMenu);
        });
        domHelperService.initializeEventListener('.content-draggable-field', 'click', 'showFieldPopover');
        domHelperService.initializeEventListener('.text-tag-editor,.text-field-input,.text-field-textarea', 'keyup', 'updateFieldOnKeyUp', { role_id: 0 });
        domHelperService.initializeEventListener('.text-tag-editor,.text-field-input,.text-field-textarea', 'blur', 'updateFieldOnBlur', { role_id: 0 });
        this._fieldRenderService.setMarkAsRenderedFields({ ...this.globalService.fields_data });
        this.productTable.refreshProductTableEvents();
    }

    updatePDFBlockHeight(id: number, currentRoute: string) {
        const pdfBlocks = document.querySelectorAll('.pdf-block');
        pdfBlocks.forEach((pdfBlock: HTMLElement) => {
            const editorUuid = pdfBlock.querySelector('.content-box-inner')?.getAttribute('editor-uuid');
            const blockData = this.globalService.fields_data['blocks'][editorUuid];
            if (blockData) {
            const pdfUrl = this.sessionImageService.getTemplateImages(blockData?.url, Number(id), this.globalService.getTheSectionByRoute(currentRoute).toLowerCase());
            this.domHandlersService.setPdfBlockHeight(pdfUrl, blockData.uuid);
            }
        });
    }

    /**
    * this is for temp solution it will be removed in future
     */
    setTheBlockSortElement(){
      const contentBoxEnter = document.querySelectorAll('.content-box-enter');
      if (contentBoxEnter.length) {
        contentBoxEnter.forEach((contentEnter: HTMLElement) => {
          this.renderer.removeClass(contentEnter, 'content-box-enter');
        })
      }
    }

    triggerEditorSideMenu(fieldSettings: FieldSettings, menu: string) {
        this.editorEmitterService.triggerOpenMenuAction({ rightMenuType: menu, fieldSettings });
        this.domHandlersService.removeAllBlockActionMenus();
    }
}